import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { Heart, Plus, Trash2 } from 'lucide-react';
/**
 * Markdown Checklist Editor with heart icons
 * Supports:
 * - [ ] Incomplete item (open heart icon)
 * - [x] Completed item (filled red-pink heart icon)
 * - # Headers
 * - **bold**, *italic*, `code`
 * - Regular text
 */
export function MarkdownChecklistEditor({ value, onChange, placeholder = 'Write your goals (supports markdown and checklists)...' }) {
    const [items, setItems] = useState([]);
    // Parse markdown to extract checklist items
    useEffect(() => {
        parseMarkdown(value);
    }, [value]);
    const parseMarkdown = (markdown) => {
        const lines = markdown.split('\n');
        const parsedItems = [];
        lines.forEach((line, index) => {
            // Match [ ] or [x] patterns
            const incompleteMatch = line.match(/^\s*-\s*\[\s*\]\s*(.+)$/);
            const completedMatch = line.match(/^\s*-\s*\[x\]\s*(.+)$/i);
            if (incompleteMatch) {
                parsedItems.push({
                    id: `item-${index}`,
                    text: incompleteMatch[1].trim(),
                    completed: false,
                    lineIndex: index
                });
            }
            else if (completedMatch) {
                parsedItems.push({
                    id: `item-${index}`,
                    text: completedMatch[1].trim(),
                    completed: true,
                    lineIndex: index
                });
            }
        });
        setItems(parsedItems);
    };
    const toggleItem = (lineIndex) => {
        const lines = value.split('\n');
        const line = lines[lineIndex];
        if (line) {
            if (line.includes('[ ]')) {
                lines[lineIndex] = line.replace(/\[\s*\]/, '[x]');
            }
            else if (line.match(/\[x\]/i)) {
                lines[lineIndex] = line.replace(/\[x\]/i, '[ ]');
            }
            onChange(lines.join('\n'));
        }
    };
    const updateItemText = (lineIndex, newText) => {
        const lines = value.split('\n');
        const line = lines[lineIndex];
        if (line && newText.trim()) {
            const isCompleted = line.match(/\[x\]/i);
            const checkbox = isCompleted ? '[x]' : '[ ]';
            lines[lineIndex] = `- ${checkbox} ${newText.trim()}`;
            onChange(lines.join('\n'));
        }
    };
    const deleteItem = (lineIndex) => {
        const lines = value.split('\n');
        lines.splice(lineIndex, 1);
        onChange(lines.join('\n'));
    };
    const addItem = () => {
        const lines = value.trim() ? value.split('\n') : [];
        lines.push('- [ ] New goal');
        onChange(lines.join('\n'));
    };
    return (_jsxs("div", { className: "w-full space-y-3", children: [_jsxs("div", { className: "p-3 rounded border space-y-2", style: {
                    backgroundColor: 'var(--bg-base)',
                    borderColor: 'var(--border-color)',
                    minHeight: '200px'
                }, children: [items.length > 0 ? (items.map((item) => (_jsxs("div", { className: "flex items-center gap-2 group p-2 rounded hover:bg-white/5 transition-colors", children: [_jsx("button", { onClick: () => toggleItem(item.lineIndex), className: "flex-shrink-0 p-1 rounded hover:bg-white/10 transition-colors", children: _jsx(Heart, { className: `w-4 h-4 transition-colors ${item.completed ? 'fill-red-400' : ''}`, style: {
                                        color: item.completed ? '#ff69b4' : 'var(--text-tertiary)',
                                    } }) }), _jsx("input", { type: "text", value: item.text, onChange: (e) => updateItemText(item.lineIndex, e.target.value), className: "flex-1 min-w-0 bg-transparent border-none outline-none text-sm overflow-hidden", style: {
                                    color: 'var(--text-primary)',
                                    textDecoration: item.completed ? 'line-through' : 'none',
                                    opacity: item.completed ? 0.6 : 1
                                } }), _jsx("button", { onClick: () => deleteItem(item.lineIndex), className: "flex-shrink-0 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-500/20 transition-all", children: _jsx(Trash2, { className: "w-4 h-4 text-red-400" }) })] }, item.id)))) : (_jsx("p", { style: { color: 'var(--text-tertiary)', fontSize: '0.875rem', padding: '8px' }, children: placeholder })), _jsxs("button", { onClick: addItem, className: "w-full mt-2 py-2 rounded flex items-center justify-center gap-2 text-sm hover:bg-white/10 transition-colors group", style: { color: 'var(--text-secondary)' }, children: [_jsx(Plus, { className: "w-4 h-4 opacity-60 group-hover:opacity-100" }), _jsx("span", { children: "Add Goal" })] })] }), _jsx("div", { className: "text-xs", style: { color: 'var(--text-tertiary)' }, children: _jsxs("details", { className: "cursor-pointer", children: [_jsx("summary", { className: "font-medium", children: "Markdown Help" }), _jsx("div", { className: "mt-2 p-2 rounded", style: { backgroundColor: 'var(--bg-base)' }, children: _jsxs("ul", { className: "space-y-1", children: [_jsx("li", { children: "Click \u2764 to mark goals as complete" }), _jsx("li", { children: "Edit goal text directly in the field" }), _jsx("li", { children: "Click trash icon to delete a goal" }), _jsx("li", { children: "Click \"Add Goal\" button to add new items" })] }) })] }) })] }));
}
